import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MessageSquareText, Search, Reply, Copy } from "lucide-react";
import { copyToClipboardWithToast } from "@/lib/clipboard";

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso || "";
  }
}

export default function Inquiries() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/contact-messages");
        if (alive) setMessages(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load inquiries");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return messages;
    return messages.filter((m) =>
      (m.name || "").toLowerCase().includes(s) ||
      (m.email || "").toLowerCase().includes(s) ||
      (m.phone || "").toLowerCase().includes(s) ||
      (m.message || "").toLowerCase().includes(s)
    );
  }, [messages, q]);

  return (
    <div className="space-y-6" data-testid="inquiries-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Studio Manager</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-1">Inquiries</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Messages submitted via the public Contact form.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, message…"
            className="w-full pl-9 pr-3 h-10 bg-card border border-border text-sm focus:outline-none focus:border-foreground/60"
            data-testid="inquiries-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center" data-testid="inquiries-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading inquiries…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center" data-testid="inquiries-empty">
          <MessageSquareText className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-serif text-xl">
            {messages.length === 0 ? "No inquiries yet." : "No matching inquiries."}
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            {messages.length === 0
              ? "When someone submits the Contact form, their message will show up here."
              : "Try a different search term."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            {filtered.length} of {messages.length} inquiries
          </div>
          {filtered.map((m) => (
            <article
              key={m.id}
              className="border border-border bg-card p-5 hover:border-foreground/40 transition-colors"
              data-testid={`inquiry-${m.id}`}
            >
              <header className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-serif text-xl">{m.name}</div>
                  <div className="mt-1.5 flex items-center gap-4 flex-wrap text-sm text-muted-foreground">
                    <a
                      href={`mailto:${m.email}`}
                      className="inline-flex items-center gap-1.5 hover:text-foreground"
                      data-testid={`inquiry-email-${m.id}`}
                    >
                      <Mail className="w-3.5 h-3.5" /> {m.email}
                    </a>
                    {m.phone && (
                      <a
                        href={`tel:${m.phone}`}
                        className="inline-flex items-center gap-1.5 hover:text-foreground"
                      >
                        <Phone className="w-3.5 h-3.5" /> {m.phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground shrink-0">
                  {formatWhen(m.created_at)}
                </div>
              </header>
              <p className="mt-4 text-sm leading-relaxed text-foreground whitespace-pre-wrap">
                {m.message}
              </p>
              <footer className="mt-4 pt-4 border-t border-border flex items-center gap-3 flex-wrap">
                <a
                  href={`mailto:${m.email}?subject=Re: your inquiry&body=Hi ${encodeURIComponent(m.name || "")},%0D%0A%0D%0A`}
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-xs uppercase tracking-[0.18em] bg-foreground text-background hover:bg-foreground/90"
                  data-testid={`inquiry-reply-${m.id}`}
                >
                  <Reply className="w-3.5 h-3.5" /> Reply
                </a>
                <button
                  type="button"
                  onClick={() => copyToClipboardWithToast(m.email, "Email copied")}
                  className="inline-flex items-center gap-1.5 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60"
                  data-testid={`inquiry-copy-email-${m.id}`}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy email
                </button>
              </footer>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
